import React from 'react';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import type { InputLocation } from "../models/AppLocation";

export interface LocationData {
    id: string;
    parentId: string;
    locationName: string;
    type: string;
    children?: LocationData[];
}

export interface LocationNode {
    id: string;
    locationName: string;
    children?: LocationNode[];
}

interface Props {
    locations: InputLocation[];
    selectedId?: string;
    onItemClick?: (id: string) => void;
}

export default function LocationTreeViewer({ locations, selectedId, onItemClick }: Props) {

    function getParentChainFromTree(
        tree: InputLocation[],
        targetId: string,
        path: string[] = []
    ): string[] | null {
        for (const node of tree) {
            const nodeId = String(node.id);

            if (nodeId === targetId) {
                return path;
            }

            if (node.children?.length) {
                const result = getParentChainFromTree(
                    node.children,
                    targetId,
                    [...path, nodeId]
                );

                if (result) return result;
            }
        }

        return null;
    }
    function getAllDescendantIds(node: InputLocation): string[] {
        const ids: string[] = [];

        if (node.children?.length) {
            for (const child of node.children) {
                const childId = String(child.id);

                ids.push(childId);
                ids.push(...getAllDescendantIds(child));
            }
        }

        return ids;
    }


    const [expanded, setExpanded] = React.useState<string[]>([]);

    React.useEffect(() => {

        if (selectedId && locations.length > 0) {
            setSelected(selectedId);

            const chain = getParentChainFromTree(locations, selectedId) ?? [];
            setExpanded((prev) => [...new Set([...prev, ...chain])]);

            
        }
    }, [selectedId, locations]);

    const [selected, setSelected] = React.useState<string | null>(selectedId ?? null);

    const handleSelect = (
        event: React.SyntheticEvent | null,
        nodeId: string) => {
        setSelected(nodeId);
        onItemClick?.(nodeId);

        const findNodeById = (nodes: InputLocation[]): InputLocation | null => {
            for (const node of nodes) {
                if (node.id === nodeId) return node;
                if (node.children) {
                    const childResult = findNodeById(node.children);
                    if (childResult) return childResult;
                }
            }
            return null;
        };

        const selectedNode = findNodeById(locations);
        if (selectedNode) {
            const childIds = getAllDescendantIds(selectedNode);
            setExpanded((prev) => [...new Set([...prev, nodeId, ...childIds])]);
        }
    };

    return (
        //<div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ccc', padding: '0.5rem', borderRadius: '4px' }}>
        <RichTreeView
            selectedItems={selected}
            expandedItems={expanded}
            items={locations}
            onItemClick={handleSelect}
            itemChildrenIndentation={24}
            checkboxSelection={true}
            sx={{
                maxHeight: 100,
                overflowY: 'auto',
                border: '1px solid #ccc',
                borderRadius: 1, p: 1,
                '& .MuiTreeItem-label': { fontSize: '0.7rem' },
                '& .MuiTreeItem-content': { py: 0.1 }
            }}
            />
    );
}
